'use client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Session } from '@/lib/getSessionPages';
import PlayersBoxComponent from '@/modules/league/new-components/rsvp-components/PlayersBoxComponent/PlayersBoxComponent';
import RsvpChart from '@/modules/league/new-components/rsvp-components/RsvpChart';
import { GameDayInfo, Player } from '@/modules/league/new-components/types';
import RSVPSLinks from '@/modules/league/new-components/rsvp-components/RSVPSLinks';
import { useTotalPlayersInSession } from '@/modules/league/TotalPlayersInSession';

interface RsvpsContentProps {
  headerContent: GameDayInfo;
  params: { slug: string; sessionId: string; matchDateId: string };
  user?: Session;
  players: {
    playingList: { players: Player[]; totalPlayers: number };
    notplayingList: { players: Player[]; totalPlayers: number };
    waitList: { players: Player[]; totalPlayers: number };
  };
  isClubMember?: boolean;
  isAuthorized: boolean;
}

const RsvpsContent = ({
  headerContent,
  params,
  user,
  players,
  isClubMember,
  isAuthorized,
}: RsvpsContentProps) => {
  const { details } = headerContent;
  const { playingList, notplayingList, waitList } = players;
  // Determine if the waitlist should be active (depends on the type of league )
  const isWaitlistActive = details?.isWaitlist && details?.maxPlayers !== -1;

  const searchParams = useSearchParams();
  const router = useRouter();
  const { setTotalPlayersInSession } = useTotalPlayersInSession();

  const successOptIn = searchParams?.get('svoi');

  //svoi is param that will be passed from pbrackets after opt in is successfully done
  useEffect(() => {
    if (successOptIn === '1') {
      toast.success('Opt in is successfully done');
      if (searchParams) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('svoi');
        router.replace(`?${newParams.toString()}`, { scroll: false });
      }
    }
  }, [successOptIn, searchParams, router]);

  useEffect(() => {
    const totalPlayersInSession =
      notplayingList.totalPlayers + playingList.totalPlayers + waitList?.totalPlayers;
    setTotalPlayersInSession(totalPlayersInSession);
  }, [players]);

  const [selectedTab, setSelectedTab] = useState('Playing');

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const renderBoxComponent = (
    players: Player[],
    cardName: string,
    leagueId: string,
    slug: string,
    sessionId: string,
    user?: Session,
    playingCounter?: number,
    maxPlayingCount?: number
  ) => {
    return players ? (
      <div id={cardName}>
        <PlayersBoxComponent
          players={players}
          headerContent={headerContent}
          cardName={cardName}
          user={user}
          playingCounter={playingCounter || 0}
          maxPlayingCount={maxPlayingCount || 0}
          isClubMember={isClubMember}
          isAuthorized={isAuthorized}
          leagueId={leagueId}
          slug={slug}
          sessionId={sessionId}
        />
      </div>
    ) : null;
  };

  const getLinkClass = (label: string) =>
    `flex-1 border-b-4 px-1 py-2 text-center text-base font-bold cursor-pointer ${
      selectedTab === label
        ? 'border-leagues-700 text-leagues-700'
        : 'text-gray-500 hover:border-leagues-300/50 hover:text-leagues-300'
    }`;

  return (
    <div className='md:gap-3pt-11 mx-auto h-auto w-full max-w-screen-xl gap-8 md:flex lg:w-4/5'>
      <div className='maw-w-[100%] mx-auto w-full lg:gap-[24px]'>
        <div className='flex grow flex-col'>
          <RsvpChart
            playingPlayers={playingList.totalPlayers}
            notPlayingPlayers={notplayingList.totalPlayers}
            waitlistPlayers={waitList.totalPlayers}
            isWaitlist={isWaitlistActive}
          />

          <RSVPSLinks
            playingPlayers={playingList.players}
            headerContent={headerContent}
            waitListPlayers={waitList.players}
            notPlayingPlayers={notplayingList.players}
            params={params}
            isAuthorized={isAuthorized}
          />

          <div className='sticky top-0 z-10 flex w-full px-4 shadow md:hidden bg-white'>
            <div className='flex w-full justify-around'>
              {[
                { label: 'Playing', exists: playingList.players.length > 0 },
                {
                  label: 'Waitlist',
                  exists: waitList.players && details.isWaitlist && isWaitlistActive,
                },
                { label: 'Not Playing', exists: notplayingList.players.length > 0 },
              ].map((link, i) =>
                link.exists ? (
                  <div
                    key={`${link.label}-${i}`}
                    className={getLinkClass(link.label)}
                    onClick={() => {
                      scrollToSection(link.label);
                      setSelectedTab(link.label);
                    }}
                  >
                    {link.label}
                  </div>
                ) : null
              )}
            </div>
          </div>

          <div className='justify-left flex flex-wrap p-3'>
            <div className='flex w-full flex-col px-1 py-2 md:w-1/2 lg:w-1/3'>
              {renderBoxComponent(
                playingList.players,
                'Playing',
                details.LeagueID,
                params.slug,
                params.sessionId,
                user,
                playingList.totalPlayers ?? 0,
                details?.maxPlayers
              )}
            </div>
            {waitList.players && details.isWaitlist && isWaitlistActive && (
              <div className='flex w-full flex-col px-1 py-2 md:w-1/2 lg:w-1/3'>
                {renderBoxComponent(
                  waitList.players,
                  'Waitlist',
                  details.LeagueID,
                  params.slug,
                  params.sessionId,
                  user,
                  playingList.totalPlayers,
                  details?.maxPlayers
                )}
              </div>
            )}
            <div className='flex w-full flex-col px-1 py-2 md:w-1/2 lg:w-1/3'>
              {renderBoxComponent(
                notplayingList.players,
                'Not Playing',
                details.LeagueID,
                params.slug,
                params.sessionId,
                user,
                playingList.totalPlayers,
                details?.maxPlayers
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RsvpsContent;
